import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { ArrowLeft, Shield, Lock, Eye, Download, Trash2, AlertCircle } from "lucide-react";

interface PrivacyProps {
  onBackToLanding: () => void;
}

export function Privacy({ onBackToLanding }: PrivacyProps) {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBackToLanding}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Home</span>
              </Button>
              <div className="h-6 w-px bg-border" />
              <div>
                <h1 className="text-2xl font-semibold">Privacy Policy</h1>
                <p className="text-sm text-muted-foreground">
                  How we protect and handle your data
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-12 max-w-4xl">
        {/* Last Updated */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-muted px-4 py-2 rounded-lg mb-4">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">Last updated: June 26, 2025</span>
          </div>
          <h2 className="text-3xl font-medium mb-4">Privacy Policy</h2>
          <p className="text-lg text-muted-foreground">
            Your privacy is fundamental to how we build and operate Decisionr.xyz. 
            This policy explains how we collect, use, and protect your information.
          </p>
        </div>

        {/* Key Principles */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[
            { icon: Shield, title: "Privacy by Design", desc: "Privacy is built into our platform from the ground up" },
            { icon: Lock, title: "Secure Storage", desc: "Your data is encrypted and stored securely" },
            { icon: Eye, title: "Full Transparency", desc: "You always know what data we have and how it's used" }
          ].map((item, index) => (
            <Card key={index} className="text-center">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">{item.title}</CardTitle>
                <CardDescription>{item.desc}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Information We Collect</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Account Information</h4>
                <p className="text-muted-foreground">Email address, name, and password when you create an account.</p>
              </div>
              <div>
                <h4 className="font-medium mb-2">Decision Data</h4>
                <p className="text-muted-foreground">The decisions, ratings, reviews, and related information you choose to record in our platform.</p>
              </div>
              <div>
                <h4 className="font-medium mb-2">Usage Analytics</h4>
                <p className="text-muted-foreground">Basic usage patterns to improve our service (page views, feature usage, error logs).</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>How We Use Your Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2">
                <li className="flex items-start"><span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0" />Provide and improve our decision tracking services</li>
                <li className="flex items-start"><span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0" />Send important account and service updates</li>
                <li className="flex items-start"><span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0" />Analyze usage patterns to improve the platform</li>
                <li className="flex items-start"><span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0" />Provide customer support when requested</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Information Sharing</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                <strong>We never sell your personal data.</strong> We only share information in these limited circumstances:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start"><span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0" />With service providers who help us operate the platform (all under strict data protection agreements)</li>
                <li className="flex items-start"><span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0" />If required by law or to protect our legal rights</li>
                <li className="flex items-start"><span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0" />With your explicit consent for specific purposes</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Your Rights and Controls</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Download className="h-5 w-5 text-primary" />
                    <div>
                      <h4 className="font-medium">Data Export</h4>
                      <p className="text-sm text-muted-foreground">Download all your data anytime</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Trash2 className="h-5 w-5 text-primary" />
                    <div>
                      <h4 className="font-medium">Account Deletion</h4>
                      <p className="text-sm text-muted-foreground">Delete your account and all data</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Eye className="h-5 w-5 text-primary" />
                    <div>
                      <h4 className="font-medium">Data Access</h4>
                      <p className="text-sm text-muted-foreground">View what data we have about you</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Lock className="h-5 w-5 text-primary" />
                    <div>
                      <h4 className="font-medium">Data Correction</h4>
                      <p className="text-sm text-muted-foreground">Update or correct your information</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Contact Us</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Questions about this privacy policy? Contact us:
              </p>
              <div className="space-y-2 text-sm">
                <p><strong>Email:</strong> privacy@decisionr.xyz</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <footer className="border-t mt-16">
        <div className="container py-8">
          <div className="text-center text-sm text-muted-foreground">
            <p>© 2025 Decisionr.xyz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}